import { API_BASE } from '@/app/config';
import type { MeetingData } from '@/app/queries/MeetingQuery/context';
import { MeetingFetchError } from '@/app/queries/MeetingQuery/fetchMeeting';
import { PAGE_INSTANCE_ID } from '@/lib/pageInstance';
import SentryImportPromise from '@/sentry/importPromise';

interface JoinTicketResponse {
  uid?: string;
  token?: string;
  screen_share_uid?: string;
  screen_share_token?: string;
}

export interface JoinTicket {
  uid: number;
  token: string;
  screen_share_uid: number;
  screen_share_token: string;
}

export const participantIdTicketOf = (data: MeetingData): JoinTicket => ({
  uid: data.meeting_data.user_id,
  token: data.token,
  screen_share_uid: data.meeting_data.screen_share_id,
  screen_share_token: data.screen_share_token,
});

export const JOIN_TICKET_TIME_LIMIT_MS = 5000;

class JoinTicketTimeoutError extends Error {}

class MalformedJoinTicketError extends Error {}

const withinTimeLimit = <T>(limitMs: number, run: (signal: AbortSignal) => Promise<T>): Promise<T> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeLimit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new JoinTicketTimeoutError(`No join ticket within ${limitMs} ms`));
    }, limitMs);
  });

  return Promise.race([run(controller.signal), timeLimit]).finally(() => clearTimeout(timer));
};

const isRtcUid = (uid: number) => Number.isInteger(uid) && uid > 0;

const isUsable = (ticket: JoinTicket) =>
  isRtcUid(ticket.uid) && isRtcUid(ticket.screen_share_uid) && Boolean(ticket.token && ticket.screen_share_token);

const joinTicketFrom = (response: JoinTicketResponse): JoinTicket => {
  const ticket = {
    uid: Number(response.uid),
    token: response.token ?? '',
    screen_share_uid: Number(response.screen_share_uid),
    screen_share_token: response.screen_share_token ?? '',
  };

  if (!isUsable(ticket)) {
    throw new MalformedJoinTicketError(`Join ticket without a usable uid and token, fields: ${Object.keys(response)}`);
  }

  return ticket;
};

const requestJoinTicket = async(hash: string, signal: AbortSignal): Promise<JoinTicket> => {
  const url = `${API_BASE}/meeting/${hash}/join/?page_instance=${PAGE_INSTANCE_ID}`;
  const response = await fetch(url, { method: 'POST', signal });

  if (!response.ok) {
    throw new MeetingFetchError(`Failed to issue a join ticket: ${response.statusText}`, response.status);
  }

  return joinTicketFrom(await response.json() as JoinTicketResponse);
};

const fetchJoinTicket = (hash: string) =>
  withinTimeLimit(JOIN_TICKET_TIME_LIMIT_MS, signal => requestJoinTicket(hash, signal));

const isMalformedTicket = (error: unknown) =>
  error instanceof MalformedJoinTicketError || error instanceof SyntaxError;

const whyTicketWasNotIssued = (error: unknown) => {
  if (error instanceof MeetingFetchError) {
    return String(error.status);
  }

  if (error instanceof JoinTicketTimeoutError) {
    return 'timeout';
  }

  if (isMalformedTicket(error)) {
    return 'malformed';
  }

  return 'network';
};

const reportTicketNotIssued = (error: unknown) => {
  const reason = whyTicketWasNotIssued(error);

  SentryImportPromise
    .then(({ default: Sentry }) => {
      Sentry.captureException(error, {
        level: 'warning',
        tags: { operation: 'join-ticket' },
        fingerprint: ['join-ticket', reason],
      });
    })
    .catch(() => undefined);
};

const issueJoinTicketOrFallBackToParticipantId = async(hash: string, data: MeetingData): Promise<JoinTicket> => {
  try {
    return await fetchJoinTicket(hash);
  } catch (error) {
    reportTicketNotIssued(error);
    return participantIdTicketOf(data);
  }
};

export default issueJoinTicketOrFallBackToParticipantId;
